/**
 * Base API Utilities for Booking Review APIs
 *
 * Shared error handling, response transformation, and fetch logic.
 * Also includes common CB payload builders for all hotel APIs.
 */
import { logger } from '@/shared/utils/logger';
import { buildUrl } from '@/api/utils';
import { generateUUID, getPat, getJwtToken } from '@/shared/utils';
import { getAllMMTCookies } from '@/shared/utils/appCookies';
import type { CBUrlParamsFull, CBUrlParamsLite, CBBaseHeaders } from '@contracts';

// ============================================
// Types
// ============================================

export interface ApiErrorInfo {
  code: string;
  message: string;
}

// ============================================
// CB Context - Shared cookie extraction
// ============================================

/**
 * Context values extracted from cookies with fallbacks
 * Used by all CB payload builders
 */
export interface CBContext {
  readonly language: string;
  readonly currency: string;
  readonly countryCode: string;
  readonly region: string;
  readonly authToken: string;
  readonly deviceId: string;
  readonly uuid: string;
}

/**
 * Get common context values from cookies with fallbacks
 * This is the single source of truth for cookie extraction
 */
export function getCBContext(): CBContext {
  const cookies = getAllMMTCookies();

  const language = cookies.language ?? 'eng';
  const currency = cookies.currency ?? 'INR';
  const countryCode = cookies.countryCode ?? 'IN';
  const region = countryCode.toLowerCase();
  const authToken = cookies.auth ?? '';
  const deviceId = cookies.deviceId ?? generateUUID();
  const uuid = cookies.uuid ?? '';

  return { language, currency, countryCode, region, authToken, deviceId, uuid };
}

// ============================================
// CB URL Params Builders
// ============================================

/**
 * Build full URL params (with requestId and idContext)
 * Used by: booking-review, policy-rules, update-price, validate-coupon, checkout
 */
export function buildCBUrlParamsFull(requestId?: string): CBUrlParamsFull {
  const { language, currency, countryCode, region } = getCBContext();
  return {
    requestId: requestId ?? generateUUID(),
    language,
    region,
    currency,
    idContext: 'B2C',
    countryCode,
  };
}

/**
 * Build lite URL params (without requestId, idContext, countryCode)
 * Used by: country-codes, currency-config
 */
export function buildCBUrlParamsLite(): CBUrlParamsLite {
  const { language, currency, region } = getCBContext();
  return { language, region, currency };
}

// ============================================
// CB Headers Builder
// ============================================

/**
 * Build standard CB headers — mirrors the flights `buildHeaders()` pattern.
 * Each auth header is included independently when its value is truthy.
 * Never throws.
 */
export function buildCBHeaders(): CBBaseHeaders {
  const { currency, language, authToken, deviceId, uuid } = getCBContext();

  const headers: CBBaseHeaders = {
    'content-type': 'application/json',
    currency,
    language,
  };

  if (authToken) {
    headers['X-Gommt-Mmt-Auth'] = authToken;
  }

  if (uuid) {
    headers['X-Gommt-Uuid'] = uuid;
  }

  if (deviceId) {
    headers['X-Device-Id'] = deviceId;
  }

  const pat = getPat();
  if (pat) {
    headers['X-Gommt-Pat'] = pat;
  }

  const jwtToken = getJwtToken();
  if (jwtToken) {
    headers['Authorization'] = `Bearer ${jwtToken}`;
  }

  return headers;
}

/**
 * Build CB headers for direct use in fetch() calls
 * Filters out undefined values to be compatible with fetch HeadersInit
 * @returns Headers object with no undefined values
 */
export function buildCBHeadersForFetch(): Record<string, string> {
  const headers = buildCBHeaders();
  return Object.fromEntries(Object.entries(headers).filter(([, v]) => v !== undefined)) as Record<
    string,
    string
  >;
}

// ============================================
// Generic CB Payload Builder
// ============================================

/** Variants of URL params supported */
export type CBUrlParamsVariant = 'full' | 'lite';

/**
 * Generic CB payload builder
 * Composes urlParams, headers, and requestBody into standard CB structure
 *
 * @param requestBody - The request body specific to the API
 * @param variant - Type of URL params to use ('full' | 'lite')
 * @param requestId - Optional requestId (auto-generated if not provided for 'full' variant)
 */
export function buildGenericCBPayload<T>(
  requestBody: T,
  variant: CBUrlParamsVariant = 'full',
  requestId?: string,
): {
  urlParams: CBUrlParamsFull | CBUrlParamsLite;
  headers: CBBaseHeaders;
  requestBody: T;
} {
  let urlParams: CBUrlParamsFull | CBUrlParamsLite;

  switch (variant) {
    case 'lite':
      urlParams = buildCBUrlParamsLite();
      break;
    case 'full':
    default:
      urlParams = buildCBUrlParamsFull(requestId);
      break;
  }

  return {
    urlParams,
    headers: buildCBHeaders(),
    requestBody,
  };
}

export interface BaseApiResponse<T> {
  success: boolean;
  data: T;
  error?: ApiErrorInfo;
}

interface FetchOptions {
  /** API endpoint URL */
  endpoint: string;
  /** Query parameters */
  queryParams: Record<string, string>;
  /** Request body (for POST requests) */
  body?: unknown;
  /** HTTP method (defaults to POST) */
  method?: 'GET' | 'POST';
  /** Logger tag for debugging */
  logTag: string;
  /** Optional custom headers (merged with default Content-Type) */
  headers?: Record<string, string>;
}

// ============================================
// Base Fetch Function
// ============================================

/**
 * Generic fetch function with standardized error handling
 * Automatically includes mmt-auth and X-Gommt-Uuid headers
 *
 * @returns Parsed JSON response or null on failure
 */
export async function baseFetch<TResponse>(
  options: FetchOptions,
): Promise<{ data: TResponse | null; error: ApiErrorInfo | null }> {
  const { endpoint, queryParams, body, method = 'POST', logTag, headers: customHeaders } = options;

  const url = buildUrl(endpoint, queryParams);

  logger.warn(`[${logTag}] Fetching:`, { ...queryParams });

  // Build headers with auth - merge custom headers with CB headers
  const cbHeaders = buildCBHeaders();
  const fetchHeaders = Object.fromEntries(
    Object.entries({ ...cbHeaders, ...customHeaders }).filter(([, v]) => v !== undefined),
  ) as Record<string, string>;

  try {
    const fetchOptions: RequestInit = {
      method,
      headers: fetchHeaders,
    };

    if (body) {
      fetchOptions.body = JSON.stringify(body);
    }

    const response = await fetch(url, fetchOptions);

    // Parse response body regardless of status
    const data = (await response.json()) as TResponse | null;

    if (!response.ok) {
      logger.error(`[${logTag}] API returned error status:`, response.status, data);

      // Try to extract error message from various response formats
      const errorData = data as {
        error?: ApiErrorInfo;
        errors?: Array<{ error_message?: string; error_code?: string; message?: string }>;
        errorDetails?: { errorMessage?: string; errorDescription?: string; errorCode?: string };
        message?: string;
        errorMessage?: string;
      } | null;

      // Format 1: Single error object
      if (errorData?.error) {
        return { data: null, error: errorData.error };
      }

      // Format 2: Errors array (common in hotel APIs)
      if (errorData?.errors && errorData.errors.length > 0) {
        const firstError = errorData.errors[0];
        return {
          data: null,
          error: {
            code: firstError?.error_code ?? 'API_ERROR',
            message: firstError?.error_message ?? firstError?.message ?? 'Request failed',
          },
        };
      }

      // Format 3: errorDetails object (common in CB APIs)
      if (errorData?.errorDetails) {
        return {
          data: null,
          error: {
            code: errorData.errorDetails.errorCode ?? 'API_ERROR',
            message:
              errorData.errorDetails.errorMessage ??
              errorData.errorDetails.errorDescription ??
              'Request failed',
          },
        };
      }

      // Format 4: Direct message field
      if (errorData?.message ?? errorData?.errorMessage) {
        return {
          data: null,
          error: {
            code: 'API_ERROR',
            message: errorData?.message ?? errorData?.errorMessage ?? 'Request failed',
          },
        };
      }

      // Fallback to generic HTTP error
      return {
        data: null,
        error: { code: 'HTTP_ERROR', message: `Something went wrong. Please try again.` },
      };
    }

    if (!data) {
      logger.warn(`[${logTag}] Null response from API`);
      return {
        data: null,
        error: { code: 'NULL_RESPONSE', message: 'Empty response from server' },
      };
    }

    return { data, error: null };
  } catch (error) {
    logger.error(`[${logTag}] Request failed:`, error);
    return {
      data: null,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network request failed',
      },
    };
  }
}

// ============================================
// Base Error Class Factory
// ============================================

export function createApiError(name: string) {
  return class extends Error {
    constructor(
      message: string,
      public readonly statusCode?: number,
      public readonly cause?: unknown,
    ) {
      super(message);
      this.name = name;
    }
  };
}
